import React, { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { BsFillXCircleFill, BsSearch } from 'react-icons/bs';
import _ from 'lodash';
import tinycolor from 'tinycolor2';
import { useDebouncedCallback } from 'use-debounce';
import { layoutDefaults } from '../utils/layout-defaults';
import { parseContainerBackgroundShade } from '../utils/manager/layout-manager';
import { useScroll } from '../hooks/use-scroll';

const iconMargin = 12;

function InputFilter({
  inputRef,
  value,
  loading,
  placeholder,
  disabled,
  maxLength,
  className,
  onChange,
  onDebounce,
  dismissOnScroll = true,
}) {
  const filterRef = useRef();
  const [isFocused, setIsFocused] = useState(false);
  const { scrollY } = useScroll({ enabled: isFocused && dismissOnScroll });

  const debounced = useDebouncedCallback(async (pValue) => {
    handleOnDebounce(pValue);
    filterRef.current = pValue;
  }, layoutDefaults.keyboardDuration);

  useEffect(() => {
    debounced(value);
  }, [value]);

  useEffect(() => {
    inputRef.current.blur();
  }, [scrollY]);

  const handleOnSubmit = async (event) => {
    event.preventDefault();
    handleOnDebounce(value);
    inputRef.current.blur();
  };

  async function handleOnDebounce(pValue) {
    if (pValue !== filterRef.current) {
      await onDebounce?.call(this, value);
    }
  }

  const handleOnClear = () => {
    if (disabled) return;

    onChange?.call(this);
  };

  const classes = useStyles({ loading });

  return (
    <div className={classes.container}>
      <form onSubmit={handleOnSubmit} className={classes.form}>
        <Form.Control
          ref={inputRef}
          value={value}
          as="input"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          enterKeyHint="search"
          inputMode="search"
          contentEditable={!loading}
          max={500}
          role="search"
          size="lg"
          disabled={disabled}
          onChange={onChange}
          placeholder={placeholder}
          className={classNames(classes.input, className)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          maxLength={maxLength}
        />
      </form>

      {loading && <Spinner className={classes.spinner} size="sm" />}
      {!loading && <BsSearch className={classes.searchIcon} />}

      {!_.isEmpty(value) && (
        <div
          className={classes.clearContainer}
          onClick={handleOnClear}
          aria-hidden="true"
        >
          <BsFillXCircleFill className={classes.clearIcon} size={18} />
        </div>
      )}
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    // border: '1px solid red',
  },
  form: {
    width: '100%',
    // border: '1px solid blue',
  },
  input: {
    color: ({ loading }) =>
      loading ? layoutDefaults.textPlaceholderColor : null,
    verticalAlign: 'text-bottom',
    opacity: 0.9,
    paddingLeft: 40,
  },
  spinner: {
    fontSize: layoutDefaults.smallerFontSize,
    position: 'absolute',
    left: iconMargin + 2,
    color: tinycolor(layoutDefaults.buttonBackgroundPrimary)
      .darken(20)
      .toHexString(),
  },
  searchIcon: {
    color: ({ layout }) => parseContainerBackgroundShade(layout, 10),
    fontWeight: 'bold',
    position: 'absolute',
    left: iconMargin + 4,
  },
  clearContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    position: 'absolute',
    right: 0,
    cursor: 'pointer',
    opacity: ({ loading }) => (loading ? layoutDefaults.smallOpacity : null),

    // border: '1px solid red',
  },
  clearIcon: {
    marginRight: iconMargin,
    color: layoutDefaults.buttonBorder,
  },
});

export { InputFilter };
